import { createSelector } from '@reduxjs/toolkit';

import { DocumentSetupLastRequesterCheckFieldState } from '@app/store/documentSetup/reducers/lastRequesterCheckField';
import { DocumentSetupLastRequesterImageFieldState } from '@app/store/documentSetup/reducers/lastRequesterImageField';
import { DocumentSetupLastRequesterTextFieldState } from '@app/store/documentSetup/reducers/lastRequesterTextField';
import RequesterCheckboxField from '@app/store/documentSetup/RequesterCheckboxField';
import RequesterImageField from '@app/store/documentSetup/RequesterImageField';
import RequesterTextField from '@app/store/documentSetup/RequesterTextField';
import { Attachment as TransportLayerAttachment } from '@app/store/transport-layer/types/attachment';
import { ParticipantReference } from '@app/store/transport-layer/types/participant';
import { RootState } from '@app/store/types';

import { requesterAttachmentsEntityAdapter } from './reducers/requesterAttachments';

export const getDocumentSetupState = (state: RootState) => state.documentSetup;

export const getRequesterTextFields = (state: RootState): RequesterTextField[] =>
  getDocumentSetupState(state).requesterTextFields;

export const getLastRequesterTextField = (
  state: RootState
): DocumentSetupLastRequesterTextFieldState => getDocumentSetupState(state).lastRequesterTextField;

export const isInsertingRequesterTextField = (state: RootState) =>
  getDocumentSetupState(state).isInsertingRequesterTextField;

export const getRequesterCheckFields = (state: RootState): RequesterCheckboxField[] =>
  getDocumentSetupState(state).requesterCheckFields;

export const getLastRequesterCheckField = (
  state: RootState
): DocumentSetupLastRequesterCheckFieldState =>
  getDocumentSetupState(state).lastRequesterCheckField;

export const isInsertingRequesterCheckField = (state: RootState) =>
  getDocumentSetupState(state).isInsertingRequesterCheckField;

export const getRequesterImageFields = (state: RootState): RequesterImageField[] =>
  getDocumentSetupState(state).requesterImageFields;

export const getLastRequesterImageField = (
  state: RootState
): DocumentSetupLastRequesterImageFieldState =>
  getDocumentSetupState(state).lastRequesterImageField;

export const isInsertingRequesterImageField = (state: RootState) =>
  getDocumentSetupState(state).isInsertingRequesterImageField;

export const getKeywords = (state: RootState) => getDocumentSetupState(state).keywords;

export const getRequesterInputIdsInPage = (state: RootState) =>
  getDocumentSetupState(state).requesterInputIdsInPage;

export const getPropsPageNumber = (_: RootState, props: { pageNumber: number }) => props.pageNumber;

export const getTrimmedFieldDataLabelMap = createSelector(
  getRequesterCheckFields,
  getRequesterImageFields,
  getRequesterTextFields,
  (checkboxFields, imageFields, textFields) => {
    const dataLabelMap: Record<string, string> = {};

    checkboxFields.forEach((checkbox) => {
      dataLabelMap[checkbox.id] = checkbox.values.dataLabel.trim();
    });
    imageFields.forEach((image) => {
      dataLabelMap[image.id] = image.values.dataLabel.trim();
    });
    textFields.forEach((text) => {
      dataLabelMap[text.id] = text.values.dataLabel.trim();
    });

    return dataLabelMap;
  }
);

/**
 * 페이지 별 요청자 텍스트 필드를 반환한다.
 */
export const getPageRequesterTextField = createSelector(
  getRequesterTextFields,
  getPropsPageNumber,
  (requesterTextFields, pageNumber) =>
    requesterTextFields.filter((v) => v.pageNumber === pageNumber)
);

/**
 * 페이지 별 요청자 체크 필드를 반환한다.
 */
export const getPageRequesterCheckField = createSelector(
  getRequesterCheckFields,
  getPropsPageNumber,
  (requesterCheckFields, pageNumber) =>
    requesterCheckFields.filter((v) => v.pageNumber === pageNumber)
);

/**
 * 페이지 별 요청자 이미지 필드를 반환한다.
 */
export const getPageRequesterImageField = createSelector(
  getRequesterImageFields,
  getPropsPageNumber,
  (requesterImageFields, pageNumber) =>
    requesterImageFields.filter((v) => v.pageNumber === pageNumber)
);

/**
 * 요청자 필드가 배치된 마지막 페이지를 반환합니다.
 */
export const getRequesterFieldMaxPage = createSelector(
  getRequesterCheckFields,
  getRequesterImageFields,
  getRequesterTextFields,
  (checkboxFields, imageFields, textFields) => {
    if (checkboxFields.length === 0 && imageFields.length === 0 && textFields.length === 0) {
      return 0;
    }

    const checkboxFieldPages = checkboxFields.map((field) => field.pageNumber);
    const imageFieldPages = imageFields.map((field) => field.pageNumber);
    const textFieldPages = textFields.map((field) => field.pageNumber);
    return Math.max(...checkboxFieldPages, ...imageFieldPages, ...textFieldPages);
  }
);

export const getRequesterInputs = createSelector(
  getRequesterCheckFields,
  getRequesterImageFields,
  getRequesterTextFields,
  (
    checkboxFields,
    imageFields,
    textFields
  ): Array<RequesterTextField | RequesterCheckboxField | RequesterImageField> => {
    return [...textFields, ...checkboxFields, ...imageFields];
  }
);

export const getRequesterInputIds = createSelector(
  getRequesterInputIdsInPage,
  (requesterInputIdsInPage) => Object.values(requesterInputIdsInPage).flat()
);

export const getRequesterInputById = (
  state: RootState,
  props: { id: string }
): RequesterCheckboxField | RequesterImageField | RequesterTextField | null => {
  return getRequesterInputs(state).find((field) => field.id === props.id) ?? null;
};

export const getRequesterAttachments = (state: RootState) =>
  requesterAttachmentsEntityAdapter
    .getSelectors()
    .selectAll(getDocumentSetupState(state).requesterAttachments);

export const getRequestMethod = (state: RootState) => getDocumentSetupState(state).requestMethod;

export const getRequesterAttachmentsPayloadForAPI = (
  state: RootState,
  props: { requesterReference: ParticipantReference }
): TransportLayerAttachment['ToSend'][] => {
  const requesterAttachments = getRequesterAttachments(state);

  return requesterAttachments.map<TransportLayerAttachment['ToSend']>((attachment) => {
    return {
      title: attachment.file.name,
      message: null,
      config: { required: true },
      dataLabel: attachment.dataLabel,
      participant: {
        id: props.requesterReference.id,
        dataLabel: props.requesterReference.dataLabel,
      },
      input: {
        file: {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          fileId: attachment.uploadedFile!.fileId,
          fileName: attachment.file.name,
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          token: attachment.uploadedFile!.token,
        },
      },
    };
  });
};
